import { Alert, Button, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ReactNode, useMemo, useState } from 'react';
import { CommodityManagementApi, MarkItViewApi } from '../../../../Apis/Apis';
import {
    BudgetPricingComponentModel,
    BudgetPricingStrategyModel,
    ComponentType,
    GetCustomerDataResponse,
    GetCustomerSettingsResponse,
    HedgeSource,
    PositionModel,
    PositionPricingComponentModel,
} from '../../../../Generated/Commodity-Management-Api';
import { MarketViewProductModel } from '../../../../Generated/Mark-It-View-Api';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { StoneXModal } from '../../../StoneXMui';
import PricingStrategyEditor, { savePricingStrategy } from '../Settings/PricingStrategyEditor';

interface MissingComponentGroup {
    key: string;
    budgetPricingStrategyId: number | null;
    positionPricingStrategyId: number | null;
    strategyName: string;
    componentTypes: ComponentType[];
    positionCount: number;
    commodityNames: string[];
    locationNames: string[];
}

interface MissingPricingComponentsPromptProps {
    positions: PositionModel[];
    onStrategySaved: () => void | Promise<void>;
}

export function componentTypeLabel(componentType: ComponentType): string {
    return componentType === ComponentType.AllIn ? 'All-In' : componentType;
}

export function groupMissingPricingComponents(positions: PositionModel[]): MissingComponentGroup[] {
    const groups = new Map<string, MissingComponentGroup>();

    for (const position of positions) {
        const missing = position.missingPricingComponents ?? [];
        if (missing.length === 0) {
            continue;
        }

        const strategy = position.positionPricingStrategy;
        const budgetPricingStrategyId = strategy?.budgetPricingStrategyId ?? null;
        const positionPricingStrategyId = strategy?.positionPricingStrategyId ? strategy.positionPricingStrategyId : null;
        const key = `${budgetPricingStrategyId ?? 'none'}:${positionPricingStrategyId ?? 'none'}`;

        const group = groups.get(key) ?? {
            key,
            budgetPricingStrategyId,
            positionPricingStrategyId,
            strategyName: strategy?.name ?? 'Unknown strategy',
            componentTypes: [],
            positionCount: 0,
            commodityNames: [],
            locationNames: [],
        };

        group.positionCount += 1;
        for (const componentType of missing) {
            if (!group.componentTypes.includes(componentType)) {
                group.componentTypes.push(componentType);
            }
        }

        const commodityName = position.commodity?.commodityName;
        if (commodityName && !group.commodityNames.includes(commodityName)) {
            group.commodityNames.push(commodityName);
        }

        const locationName = position.location?.locationName;
        if (locationName && !group.locationNames.includes(locationName)) {
            group.locationNames.push(locationName);
        }

        groups.set(key, group);
    }

    return Array.from(groups.values()).sort((a, b) => a.strategyName.localeCompare(b.strategyName));
}

function nextId(ids: number[]): number {
    return ids.reduce((max, id) => Math.max(max, id), 0) + 1;
}

export function seedMissingComponents(
    strategy: BudgetPricingStrategyModel,
    group: MissingComponentGroup,
    usesSeparatePositionStrategies: boolean,
): BudgetPricingStrategyModel {
    if (usesSeparatePositionStrategies && group.positionPricingStrategyId !== null) {
        const positionStrategies = (strategy.positionStrategies ?? []).map((positionStrategy) => {
            if (positionStrategy.positionPricingStrategyId !== group.positionPricingStrategyId) {
                return positionStrategy;
            }

            const components = [...(positionStrategy.components ?? [])];
            let id = nextId(components.map((c) => c.positionPricingComponentId));
            for (const componentType of group.componentTypes) {
                const component: PositionPricingComponentModel = {
                    positionPricingComponentId: id++,
                    positionPricingStrategyId: positionStrategy.positionPricingStrategyId,
                    componentType,
                    marketPriceProvider: undefined,
                    marketViewProductId: null,
                    settlementSymbol: null,
                } as PositionPricingComponentModel;
                components.push(component);
            }

            return { ...positionStrategy, components };
        });

        return { ...strategy, positionStrategies };
    }

    const components = [...(strategy.components ?? [])];
    let id = nextId(components.map((c) => c.budgetPricingComponentId));
    for (const componentType of group.componentTypes) {
        const component: BudgetPricingComponentModel = {
            budgetPricingComponentId: id++,
            budgetPricingStrategyId: strategy.budgetPricingStrategyId,
            componentType,
            marketPriceProvider: undefined,
            marketViewProductId: null,
            hedgeSource: HedgeSource.Positions,
        };
        components.push(component);
    }

    return { ...strategy, components };
}

function describeGroup(group: MissingComponentGroup): string {
    const components = group.componentTypes.map(componentTypeLabel).join(' and ');
    const positions = group.positionCount === 1 ? '1 position' : `${group.positionCount} positions`;
    const where = group.locationNames.length > 0 ? ` at ${group.locationNames.join(', ')}` : '';
    return `${positions}${where} need a ${components} component on "${group.strategyName}".`;
}

export default function MissingPricingComponentsPrompt(props: MissingPricingComponentsPromptProps) {
    const { positions, onStrategySaved } = props;
    const { enqueueSnackbar } = useSnackbar();

    const isAdmin = useApplicationSelector((state: RootState) => state.userAccess.isAdmin) ?? false;
    const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
    const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);
    const usesSeparatePositionStrategies = customerSettings?.separatePricingStrategiesForPositions ?? false;

    const groups = useMemo(() => groupMissingPricingComponents(positions), [positions]);

    const [modalOpen, setModalOpen] = useState(false);
    const [loadingStrategy, setLoadingStrategy] = useState(false);
    const [marketViewProducts, setMarketViewProducts] = useState<MarketViewProductModel[] | null>(null);
    const [draft, setDraft] = useState<BudgetPricingStrategyModel | null>(null);
    const [draftGroup, setDraftGroup] = useState<MissingComponentGroup | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    if (groups.length === 0) {
        return null;
    }

    const unpricedCount = groups.reduce((sum, group) => sum + group.positionCount, 0);

    function closeModal() {
        setModalOpen(false);
        setDraft(null);
        setDraftGroup(null);
    }

    async function openEditor(group: MissingComponentGroup) {
        if (group.budgetPricingStrategyId === null) {
            enqueueSnackbar('This strategy cannot be opened from here. Edit it under Settings.', { variant: 'warning' });
            return;
        }

        setLoadingStrategy(true);
        try {
            const [strategiesResponse, productsResponse] = await Promise.all([
                CommodityManagementApi.listPricingStrategies(undefined),
                marketViewProducts === null ? MarkItViewApi.listMarketViewProducts(undefined) : Promise.resolve(null),
            ]);

            if (productsResponse !== null) {
                setMarketViewProducts(productsResponse.data.rows ?? []);
            }

            const strategy = (strategiesResponse.data.rows ?? []).find((x) => x.budgetPricingStrategyId === group.budgetPricingStrategyId);
            if (!strategy) {
                enqueueSnackbar(`Pricing strategy "${group.strategyName}" was not found. It may have been deleted.`, { variant: 'error' });
                return;
            }

            setDraftGroup(group);
            setDraft(seedMissingComponents(strategy, group, usesSeparatePositionStrategies));
        } catch (error) {
            console.error('Failed to load pricing strategy', error);
            enqueueSnackbar('Failed to load the pricing strategy.', { variant: 'error' });
        } finally {
            setLoadingStrategy(false);
        }
    }

    async function saveDraft(strategy: BudgetPricingStrategyModel) {
        setIsSaving(true);
        try {
            await savePricingStrategy(strategy);
            enqueueSnackbar(`Pricing strategy "${strategy.name}" updated. Re-pricing positions.`, { variant: 'success' });
            closeModal();
            await onStrategySaved();
        } catch (error) {
            console.error('Failed to save pricing strategy', error);
            enqueueSnackbar('Failed to save pricing strategy.', { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    }

    const summary = unpricedCount === 1
        ? '1 position could not be priced because its pricing strategy is missing a component.'
        : `${unpricedCount} positions could not be priced because their pricing strategies are missing components.`;

    let modalBody: ReactNode;
    if (draft !== null && draftGroup !== null) {
        modalBody = (
            <PricingStrategyEditor
                strategy={draft}
                customerData={customerData}
                customerSettings={customerSettings}
                marketViewProducts={marketViewProducts ?? []}
                usesSeperatePricingStrategies={usesSeparatePositionStrategies}
                intro={
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {describeGroup(draftGroup)} A blank {draftGroup.componentTypes.map(componentTypeLabel).join(' and ')} component has been
                        added below. Choose its price source and save to price these positions.
                    </Alert>
                }
                onCancel={closeModal}
                onSave={saveDraft}
                onChange={setDraft}
                isWorking={isSaving}
            />
        );
    } else {
        modalBody = (
            <>
                <p>
                    {isAdmin
                        ? 'Each strategy below is missing a component that one of its positions is priced from. Add the component and the positions will be priced on the next load.'
                        : 'Each strategy below is missing a component that one of its positions is priced from. Ask an administrator to add it under Settings, Pricing Strategies.'}
                </p>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Pricing Strategy</TableCell>
                            <TableCell>Commodity</TableCell>
                            <TableCell>Locations</TableCell>
                            <TableCell>Missing Component</TableCell>
                            <TableCell align="right">Positions</TableCell>
                            {isAdmin && <TableCell />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groups.map((group) => (
                            <TableRow key={group.key}>
                                <TableCell>{group.strategyName}</TableCell>
                                <TableCell>{group.commodityNames.join(', ')}</TableCell>
                                <TableCell>{group.locationNames.join(', ')}</TableCell>
                                <TableCell>{group.componentTypes.map(componentTypeLabel).join(', ')}</TableCell>
                                <TableCell align="right">{group.positionCount}</TableCell>
                                {isAdmin && (
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            disabled={loadingStrategy || group.budgetPricingStrategyId === null}
                                            onClick={() => openEditor(group)}
                                        >
                                            Add component
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </>
        );
    }

    return (
        <>
            <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                    <Button color="inherit" size="small" onClick={() => setModalOpen(true)}>
                        Review
                    </Button>
                }
            >
                {summary}
            </Alert>

            <StoneXModal
                header={draft !== null ? 'Add Missing Pricing Component' : 'Positions Missing Pricing Components'}
                open={modalOpen}
                onClose={closeModal}
            >
                {modalBody}
            </StoneXModal>
        </>
    );
}
